import * as OSRM from './services/osrm.service'
import { nanoid } from 'nanoid'
import i18n from '@/i18n'

import { Route, RouteId, routeModel } from './models/Route'
import { Place } from './models/Place'
import { env, getFormattedDuration, getFormattedTime, toast } from '../tools'
import { DB } from './database'
import { RouteSettingsPreset, PlaceModel, Location, RouteSettings, RouteSettingsModel } from './models'
// import firebase from 'firebase'
import { store } from '@/store'
import { saveRoute } from './sync'
import { timestamp } from './tools'
import { Feedback } from './models/Feedback'

const t = i18n.global.t

export * from './sync'
export * from './stats'
export * from './live'
export * from './services/subscription.service'

// const deleteField = firebase.firestore.FieldValue.delete()

export const getRoute = (routeId: string): Route => {
  const route = store.route.getRoute(routeId)
  return route
  return {
    ...route,
    places: [ ...route.places ]
  }
}

export const generateShortId = <T>() => {
  return nanoid(8)
}
export const generateRouteId = (): RouteId => {
  return generateShortId() as RouteId
}

export const createRoute = (userId: string, routeSettings: RouteSettings): Route => {
  const route = routeModel(userId, routeSettings)
  if (store.user.getUserRoutes().length < store.user.permission('LIMIT_ROUTES')) {
    DB.createRoute(route)
    return { ...route }
  }
  throw t('errors.route_limit_reached')
} 

export const deleteRoute = async (routeId: string) => {
  return DB.deleteRoute(routeId)
}

// Calculate total route duration
const getRouteDuration = (places: Place[]): number => {
  return places[places.length - 1].times.arrival - places[0].times.departure
}

// Calculate and update arrival/departure times and return total route duration
export const calculateDuration = (route: Route): Route => { 
  const nextRoute = { ...route }
  const settings = { ...route.settings }
  const places = [ ...route.places ]
  
  const stops = places.length

  if (!stops) {
    store.map.updatePlaces([])
    return nextRoute
  }

  // Add first/last booleans for convenience
  places.forEach((place, i) => {
    place.first = i === 0
    place.last = i === stops - 1
    place.index = i
  })

  // If a timeLimit is given, distribute the remaining time between stops
  let autoBreakTime = 0
  if (settings.timeLimit > 0) {    
    const durationDriving = places.reduce((sum, place) => sum + place.duration.to, 0)
    const placesWithCustomBreaks = places.filter(place => place.breakTime >= 0 && !place.first && !place.last)
    const totalCustomBreaks = placesWithCustomBreaks.reduce((sum, place) => sum + place.breakTime, 0)

    const availableTime = settings.timeLimit - durationDriving - totalCustomBreaks
    
    if (availableTime > 0 ) {
      // Distribute the available time on stops without custom breaks
      autoBreakTime = Math.floor(availableTime / (stops - placesWithCustomBreaks.length - 2))
    } else {
      toast({
        title: t('errors.route_time_limit_exceeded.title'),
        msg: t('errors.route_time_limit_exceeded.msg', [getFormattedDuration(durationDriving), getFormattedDuration(settings.timeLimit)]),
        time: 10000,
        type: 'warning'
      })
      settings.timeLimit = 0
    }
  }  

  // Extract the starting time
  const [startHours, startMins] = settings.startTime.split(':').map(x => +x)
  const startTime = new Date().setHours(startHours, startMins, 0) / 1000

  // Add breakTime to places accordingly
  places.reduce((prevArrivalTime, place) => { 
    // Do not add breaktime to first and last stop
    const breakTime = (!place.first && !place.last) ? (place.breakTime >= 0 ? place.breakTime*1 : autoBreakTime || settings.breakTime*1) : 0
    place.times.break = breakTime
    place.times.arrival = prevArrivalTime + place.duration.to
    place.times.departure = place.times.arrival + breakTime
    return place.times.departure
  }, startTime)

  // Round place times to save bits ;)
  places.forEach(place => {
    place.times.arrival = Math.floor(place.times.arrival)
    place.times.departure = Math.floor(place.times.departure)
  })

  // Update state
  nextRoute.settings = settings
  nextRoute.saved = false
  nextRoute.duration = getRouteDuration(places)
  nextRoute.places = places
  nextRoute.timestamps.updated = timestamp()
  
  // Update map    
  store.map.updatePlaces(places)

  return nextRoute
}

export const optimizeRoute = async (route: Route): Promise<Route> => {
  const nextRoute = { ...route }
  const settings = { ...route.settings }
  let places = [ ...route.places ]
  
  if (places.length >= 2) {

    // Get route data from OSRM
    try {
      const data = await OSRM.fetchOSRMRoute(places, settings)
      const routeData = (data.trips || data.routes)[0] 
      const legs = routeData.legs
            
      // Get new place indices (undefined on custom route)
      const newIndices = data.waypoints.map(wp => wp.waypoint_index)

      // Set new place indices
      const sortedPlaces: Place[] = []
      newIndices.forEach((newIndex, oldIndex) => {
        sortedPlaces[newIndex || oldIndex] = places[oldIndex]
      })
      places = sortedPlaces
    
      // Count distances and durations
      const total = places.reduce((total, place, i) => {
        place.curbCoords = {
          lat: data.waypoints[i].location[1],
          lon: data.waypoints[i].location[0]
        }
        const leg = legs[i-1] || null
        place.distance.to = leg?.distance || 0
        place.duration.to = leg?.duration || 0
        place.distance.total = total.distance + place.distance.to
        place.duration.total = total.duration + place.duration.to
        return {
          distance: place.distance.total,
          duration: place.duration.total
        }
      }, { distance: 0, duration: 0 })

      // Update route and places in state
      nextRoute.timestamps.updated = timestamp()
      nextRoute.durationDriving = routeData.duration
      nextRoute.distance = routeData.distance
      nextRoute.places = places    
      nextRoute.calculated = true
      nextRoute.saved = false
      nextRoute.geometry.legs = OSRM.getGeometryLegs(data)      

      // Add arrival times to places
    } catch(err) {
      toast({title: 'Route server not responding.', type: 'error', msg: 'Please try another vehicle or try again later.'})
      return nextRoute
    }
  } else {
    nextRoute.geometry.legs = []
    nextRoute.distance = 0
    nextRoute.duration = 0
    nextRoute.durationDriving = 0
  }  
  return calculateDuration(nextRoute)
}

export const addPlaces = (routeId: string, places: Place[]): Promise<Route> => {
  const route = getRoute(routeId)
  if (route.live) {
    throw t('errors.cannot_add_stops_while_live')
  }
  if (route.places.length + places.length <= store.user.permission('LIMIT_ROUTE_PLACES')) {
    places.forEach(place => {
      route.places.push({ ...place })
    })
    return optimizeRoute(route)
  }
  throw t('errors.route_place_limit_exceeded')
}

export const addPlace = (routeId: string, place: Place): Promise<Route> => {
  return addPlaces(routeId, [place])
}

export const addLocation = async (routeId: string, location: Location): Promise<Route> => {
  const place = new PlaceModel(location)  
  return addPlace(routeId, place)
}

export const deletePlace = (routeId: string, placeIndex: number): Promise<Route> => {  
  const route = getRoute(routeId)
  route.places.splice(placeIndex, 1)
  return optimizeRoute(route)
}

export const validatePlaceIndex = (route: Route, fromIndex: number, toIndex: number): boolean => {
  if (route.settings.custom) {
    return true
  }
  // Place is moved to last or first
  return !(toIndex < route.places.length - 1
    && toIndex > 0
    && fromIndex < route.places.length - 1
    && fromIndex > 0)
}

export const changePlaceIndex = async (routeId: string, fromIndex: number, toIndex: number) => {  
  const route = getRoute(routeId)
  const stops = route.places.length

  // Correct toIndex if e.g. 10000 (see setPlaceLast() )
  toIndex = toIndex > stops - 1 ? stops - 1 : toIndex
  
  if (fromIndex != toIndex) {   
    // If place is moved while the route is not set to custom 
    if (!validatePlaceIndex(route, fromIndex, toIndex)) {      
      // Trigger reactivity
      //route.places = [ ...route.places ]
      throw new Error('Place index cannot be manually modified')
    } else {
      // Update index and optimize route
      route.places.splice(toIndex, 0, route.places.splice(fromIndex, 1)[0])
      return optimizeRoute(route)
    }
  }
}

// Set place to first
export const setPlaceFirst = async (routeId: string, placeIndex: number) => {
  return changePlaceIndex(routeId, placeIndex, 0)
}
// Set place to last
export const setPlaceLast = async (routeId: string, placeIndex: number) => {  
  return changePlaceIndex(routeId, placeIndex, 10000)
}

// Get the digit/letter to be displayed for a place
export const getPlaceSymbol = (routeId: string, placeIndex: number): string => {
  const route = getRoute(routeId)
  const stops = route.places.length
  return placeIndex == 0 ? 'A' : placeIndex == stops - 1 ? 'B' : (placeIndex + 1).toString()
}

export const updateSettings = (routeId: string, updatedSettings: Partial<RouteSettings>, save?: boolean) => {
  
  if (updatedSettings.private === true && !store.user.permission('FEATURE_PRIVATE_ROUTES')) {
    throw 'Private ruter er ikke en del af dit abonnement'
  }

  const route = getRoute(routeId)
  route.settings = {...route.settings, ...updatedSettings}  

  if (save) {
    return saveRoute(route)
  }
  if (updatedSettings.vehicle || updatedSettings.detailed !== undefined || updatedSettings.custom === false) {
    return optimizeRoute(route)
  }
  else if (updatedSettings.startTime !== undefined || updatedSettings.breakTime !== undefined || updatedSettings.timeLimit !== undefined) {    
    return calculateDuration(route)
  }
  if (Object.keys(updatedSettings).length) {
    route.saved = false
    return route
  }
}

export const updatePlace = (routeId: string, placeIndex: number, updatedPlace: Partial<Place>) => {
  const route = getRoute(routeId)
  const oldPlace = route.places[placeIndex]
  const newPlace = {...oldPlace, ...updatedPlace}
  route.places[placeIndex] = newPlace
  
  if (updatedPlace.address) {
    return optimizeRoute(route)
  }
  if (updatedPlace.breakTime != undefined) {
    return calculateDuration(route)
  }
  if (Object.keys(updatedPlace).length) {
    route.saved = false
    return route
  }
}

export const getRouteLink = (routeId: string, short?: boolean): string => {
  const baseUrl = short ? env('HOST_SHORT') : env('HOST')
  return `${baseUrl}/r/${routeId}`
}

export const exportRoute = (routeId: string) => {
  const route = getRoute(routeId)
  const places = route.places

  const headers = [
    t('export.headers.stop_number'),
    t('export.headers.name'),
    t('export.headers.address'),
    t('export.headers.arrival_time'),
    t('export.headers.departure_time'),
    t('export.headers.break_time')
  ]

  const rows = places.map((place, placeIndex) => [
    placeIndex + 1,
    place.name,
    place.address,
    place.first ? '-' : getFormattedTime(place.times.arrival),
    place.last ? '-' : getFormattedTime(place.times.departure),
    place.times.departure - place.times.arrival
  ])
  
  const csvContent = 'data:text/csv;charset=utf-8,' + '\uFEFF' + [headers, ...rows].map(row => row.map(col => '"'+col+'"').join(';')).join('\n')

  const encodedUri = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', encodedUri)
  link.setAttribute('download', `tripply_export_${route.id}.csv`)
  document.body.appendChild(link) // Required for FF
  
  link.click()
}


export const giveFeedback = (feedback: Feedback) => {
  return DB.createFeedback(feedback)
}

export const isRouteOwner = (route: Route, userId: string | undefined) => {
  return route.roles.owner === userId
}
export const isRouteDriver = (route: Route, userId: string | undefined) => {
  return route.roles.driver === userId
}